<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { CMD_SEND_TRANSCRIPT } from 'dashboard/helper/commandbar/events';
import { emitter } from 'shared/helpers/mitt';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';
import FeedbackModal from 'dashboard/components/widgets/conversation/FeedbackModal.vue';
import ClassifierAnalysisModal from 'dashboard/components/widgets/conversation/ClassifierAnalysisModal.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
    FeedbackModal,
    ClassifierAnalysisModal,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    isEmailInbox: {
      type: Boolean,
      default: false,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['setReplyMode', 'togglePopout', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    // Current AI draft state — AiDraftBanner publishes via wybot:draft-loaded
    // whenever it fetches the latest draft for this conversation. We mirror
    // the id/status/has_classifier locally so the toolbar buttons can
    // reflect availability without making their own API calls.
    const currentDraftId = ref(null);
    const currentDraftStatus = ref(null);
    const currentDraftHasClassifier = ref(false);
    const showFeedbackModal = ref(false);
    const showClassifierModal = ref(false);

    const onDraftLoaded = e => {
      const detail = e?.detail;
      if (!detail) return;
      // Ignore events for other conversations (shared event bus). Conversation
      // IDs are always positive integers, so `!` is a safe null/undefined check.
      if (!props.conversationId) return;
      if (detail.conversationId !== props.conversationId) return;
      currentDraftId.value = detail.draftId;
      currentDraftStatus.value = detail.status;
      currentDraftHasClassifier.value = !!detail.hasClassifier;
    };

    onMounted(() => {
      window.addEventListener('wybot:draft-loaded', onDraftLoaded);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('wybot:draft-loaded', onDraftLoaded);
    });
    watch(
      () => props.conversationId,
      () => {
        currentDraftId.value = null;
        currentDraftStatus.value = null;
        currentDraftHasClassifier.value = false;
      }
    );

    const openFeedbackModal = () => {
      showFeedbackModal.value = true;
    };

    const openClassifierModal = () => {
      if (!currentDraftHasClassifier.value) return;
      showClassifierModal.value = true;
    };

    // Opens the existing "Send conversation transcript" modal (lives in
    // MoreActions.vue at the conversation header). Emitting the command event
    // is the standard cross-component trigger for this modal.
    const openTranscriptModal = () => {
      emitter.emit(CMD_SEND_TRANSCRIPT);
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      toggleCopilotMenu,
      handleClickOutside,
      currentDraftId,
      currentDraftHasClassifier,
      showFeedbackModal,
      showClassifierModal,
      openFeedbackModal,
      openClassifierModal,
      openTranscriptModal,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div v-if="captainTasksEnabled" class="flex items-center gap-2">
      <div class="relative">
        <NextButton
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
            'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
          }"
          sm
          icon="i-ph-sparkle-fill"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="handleClickOutside"
          :has-selection="false"
          :editor-content="editorContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <NextButton
        ghost
        sm
        icon="i-ph-chat-circle-dots"
        class="text-n-slate-11 hover:enabled:!bg-n-slate-3"
        :title="$t('FEEDBACK_MODAL.TITLE')"
        @click="openFeedbackModal"
      />
      <NextButton
        ghost
        sm
        icon="i-lucide-route"
        :disabled="!currentDraftHasClassifier"
        class="hover:enabled:!bg-n-slate-3"
        :class="
          currentDraftHasClassifier
            ? 'text-n-slate-11'
            : 'text-n-slate-8 cursor-not-allowed'
        "
        :title="
          currentDraftHasClassifier
            ? $t('CLASSIFIER_MODAL.BUTTON_TITLE')
            : $t('CLASSIFIER_MODAL.BUTTON_TITLE_DISABLED')
        "
        @click="openClassifierModal"
      />
      <NextButton
        v-if="isEmailInbox"
        ghost
        sm
        icon="i-lucide-forward"
        class="text-n-slate-11 hover:enabled:!bg-n-slate-3"
        :title="$t('CONVERSATION.REPLYBOX.FORWARD_CONVERSATION')"
        @click="openTranscriptModal"
      />
      <NextButton
        ghost
        class="text-n-slate-11"
        sm
        :icon="popoutReplyBox ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'"
        @click="$emit('togglePopout')"
      />
    </div>
    <FeedbackModal
      :show="showFeedbackModal"
      :conversation-id="conversationId"
      :ai-draft-id="currentDraftId"
      @close="showFeedbackModal = false"
    />
    <ClassifierAnalysisModal
      :show="showClassifierModal"
      :conversation-id="conversationId"
      :ai-draft-id="currentDraftId"
      @close="showClassifierModal = false"
    />
  </div>
</template>
